import { DoorType } from "../../entities-generated/common/door-type";
import { InstallationState } from "../../entities-generated/common/installation-state";
import { ProjectStatus } from "../../entities-generated/common/project-status";
import { IDoorInstallationModel } from "../../entities-generated/models/project/door-installation-model";
import { InstallationSiteModel } from "../../entities-generated/models/project/installation-site-model";
import { IProjectConfiguration } from "../../entities-generated/models/project/project-configuration";
import { EventNotificationService, useStatefulEventNotification } from "../../framework/services/event-notification";
import { LocalStorageService } from "../../framework/services/local-storage";
import { any, firstOrDefault, forEach, none, orderBy, where } from "../../framework/utils/collection";
import { numberOrUndefined } from "../../framework/utils/number";
import { toKebabCase } from "../../framework/utils/string";
import { Shopability } from "../entities/shopability";
import { ShopAppEvents } from "../events";
import { DoorSortMode } from "../shop/entities/door-sort-options";

const SORT_PREF_KEY = 'Bwj.Storm.CustomerDoorSortPref';

class ProjectServiceClass {

  public allowConfigOrderException() {
    return this.configOrderException;
  }

  public registerConfigOrderException() {
    this.configOrderException = true;
  }

  public clearConfigOrderException() {
    this.configOrderException = false;
  }

  public getActiveInstallationCount(pc: IProjectConfiguration) {
    const allInstalls = this.getDoorInstallations(pc);
    const activeInstalls = where(allInstalls, i => i.isExcluded === false);
    return activeInstalls.length;
  }

  public getActiveInstallationsWithSelectedDoorCount(pc: IProjectConfiguration) {
    const allInstalls = this.getDoorInstallations(pc);
    const activeInstalls = where(allInstalls, i => i.isExcluded === false && !!i.selectedDoor);
    return activeInstalls.length;
  }

  public getDoorInstallation(location:string, doorType: DoorType, pc: IProjectConfiguration) {
    const site = firstOrDefault(pc.installLocations, i => toKebabCase(i.name) === location);
    if(!site) { return null; }
    const door = firstOrDefault(site.doorInstallations, d => d.doorType === doorType);
    return door;
  }

  public precedingDoorsConfigured(door: IDoorInstallationModel, pc: IProjectConfiguration) {
    const installs = orderBy(this.getDoorInstallations(pc), i => i.configurationIndex);
    const predecessors = where(installs, i => i.configurationIndex < door.configurationIndex);
    const configured = none(predecessors, p => p.installationState !== InstallationState.ConfigurationComplete);
    return configured;
  }

  public getNextIncompleteInstall(pc: IProjectConfiguration) {
    if(this.getShoppingState(pc) !== Shopability.Shoppable) { return null; }
    if(pc.status === ProjectStatus.ProjectPlanned) { return null; }

    const site = firstOrDefault(pc.installLocations, i => any(i.doorInstallations, i => i.installationState !== InstallationState.ConfigurationComplete));
    if(!site) { return null; }
    const door = firstOrDefault(site.doorInstallations, d => d.installationState !== InstallationState.ConfigurationComplete)!;
    const ism = new InstallationSiteModel();
    ism.name = site.name;
    ism.doorInstallations.push(door);
    return ism;
  }

  public getShoppingState(pc: IProjectConfiguration) {
    if(any(this.shoppableStates, s => pc.status === s)) { return Shopability.Shoppable; }
    if(any(this.shoppingCompleteStates, s => pc.status === s)) { return Shopability.ShoppingComplete; }
    return Shopability.Inaccessible;
  }

  public getDoorSortingPreference(): DoorSortMode {
    const pref = LocalStorageService.get<string>(SORT_PREF_KEY);
    const value = numberOrUndefined(pref) as DoorSortMode | undefined;
    if(!value) {
      LocalStorageService.set(SORT_PREF_KEY, DoorSortMode.Price.toString());
      return DoorSortMode.Price;
    }
    
    return value;
  }

  public setDoorSortingPreference(sortBy: DoorSortMode) {
    const currentPref = this.getDoorSortingPreference();
    if(sortBy === currentPref) { return; }

    LocalStorageService.set(SORT_PREF_KEY, sortBy.toString());
    EventNotificationService.dispatch(ShopAppEvents.DoorSortPreferenceDidChange, sortBy);
  }
  
  private getDoorInstallations(pc: IProjectConfiguration) {
    const installs: IDoorInstallationModel[] = [];
    forEach(pc.installLocations, location => {
      forEach(location.doorInstallations, door => {
        installs.push(door);
      });
    });

    return installs;
  }

  private configOrderException: boolean = false;

  private shoppableStates: ProjectStatus[] = [
    ProjectStatus.CatalogPublished,
    ProjectStatus.ProjectInPlanning,
    ProjectStatus.ProjectPlanned,
  ];
  private shoppingCompleteStates: ProjectStatus[] = [
    ProjectStatus.InstallationCompleted,
    ProjectStatus.InstallationInProgress,
    ProjectStatus.InstallationPurchased,
    ProjectStatus.InstallationScheduled,
  ];
}

export const ProjectService = new ProjectServiceClass();

export function useDoorSortingPreference(subscriberId: string) {
    return useStatefulEventNotification(
      ShopAppEvents.DoorSortPreferenceDidChange,
      subscriberId,
      ProjectService.getDoorSortingPreference());
}